/** Digisaka WebGIS — Privacy Policy & Terms (display copy for signup modal). */

export const POLICIES_COMPANY = 'Leads Agri Product Corporation';
export const POLICIES_SUPPORT_EMAIL = 'support@digisaka.app';

export type PolicyTab = 'privacy' | 'terms';

export interface PolicySection {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
}

export const PRIVACY_POLICY_SECTIONS: PolicySection[] = [
  {
    title: 'Introduction',
    paragraphs: [
      `${POLICIES_COMPANY} operates the Digisaka WebGIS platform ("Platform"). We comply with the Data Privacy Act of 2012 (Republic Act No. 10173) and its Implementing Rules and Regulations.`,
      'By creating an account or using the Platform, you consent to the collection and use of information as described in this Privacy Policy.',
    ],
  },
  {
    title: '1. Information We Collect',
    paragraphs: ['When you register or use Digisaka WebGIS, we may collect:'],
    bullets: [
      'Personal information: name, email address, organization, and account role.',
      'Farm information: farm names, field boundaries, size, location, and related agricultural data you submit.',
      'Media data: photos or uploaded images related to farm activities and analysis.',
      'Usage data: log data, device and browser information, API interactions, and compute usage for analysis budgets.',
      'Location data: map coordinates and GPS-related information used to identify farm areas and run geospatial analysis.',
    ],
  },
  {
    title: '2. Cookies and Session Storage',
    paragraphs: [
      'Digisaka uses essential httpOnly session cookies to keep you signed in securely after login. These cookies are required for authenticated use of the Platform.',
      'If you block or disable cookies in your browser, we cannot maintain your login session and you will need to sign in again on each visit.',
      'Session cookies are used for authentication and security, not for advertising. Theme and interface preferences may be stored locally in your browser.',
    ],
  },
  {
    title: '3. How We Use Your Information',
    bullets: [
      'Registering and verifying accounts.',
      'Providing map, satellite, forecast, and other analysis features.',
      'Enforcing compute budgets, subscriptions, and fair use of analysis tools.',
      'Communicating service updates, verification codes, and support responses.',
      'Improving user experience, reliability, and new features.',
      'Complying with legal obligations or regulatory requirements.',
    ],
  },
  {
    title: '4. Data Sharing',
    paragraphs: [
      'We do not sell your personal information. We may share data with service providers that help us operate the Platform (hosting, email, payments, Earth observation APIs, AI inference), subject to appropriate safeguards.',
      'We may disclose information when required by law or to protect the rights, safety, and security of users and the Platform.',
    ],
  },
  {
    title: '5. Data Security and Retention',
    paragraphs: [
      'We implement reasonable technical and organizational measures to protect your information. Passwords are stored using secure one-way hashing.',
      'We retain information as long as needed to provide the Service, meet legal requirements, and resolve disputes.',
    ],
  },
  {
    title: '6. Your Rights',
    paragraphs: [
      'Under applicable law, including the Data Privacy Act of 2012, you may request access to, correction of, or deletion of your personal information, subject to legal and operational requirements.',
      `Contact us at ${POLICIES_SUPPORT_EMAIL} for privacy-related requests.`,
    ],
  },
];

export const TERMS_SECTIONS: PolicySection[] = [
  {
    title: 'Introduction',
    paragraphs: [
      'Welcome to Digisaka WebGIS. By accessing or using our Platform and services, you agree to these Terms and Conditions.',
      'These Terms ensure responsible and equitable use of the Platform. If you do not accept any provision, please discontinue use of our services.',
    ],
  },
  {
    title: 'Account Registration and Security',
    bullets: [
      'You must provide accurate and complete information during account creation.',
      'You are responsible for maintaining the confidentiality of your login credentials.',
      'Any activity conducted through your account is your responsibility.',
      'Passwords are securely hashed; never share your password with others.',
    ],
  },
  {
    title: 'Session Cookies and Login',
    paragraphs: [
      'Signing in requires essential session cookies as described in our Privacy Policy. By signing up or signing in, you consent to their use for authentication.',
    ],
  },
  {
    title: 'Single Active Session Policy',
    paragraphs: [
      'For security and fair use, Daily and Monthly plans generally allow one active session per account at a time. Logging in from a new device may end your previous session.',
      'Unlimited plans may allow a limited number of concurrent devices as defined by your subscription.',
    ],
  },
  {
    title: 'Compute Budget and Fair Use',
    bullets: [
      'Analysis features consume server and cloud compute resources subject to your plan and compute budget.',
      'We record processing time for metered features to enforce quotas and billing fairness.',
      'Attempts to bypass limits, abuse guest quotas, or overload the system are prohibited.',
    ],
  },
  {
    title: 'User Conduct and Responsibilities',
    bullets: [
      'Use the Platform lawfully and responsibly.',
      'Do not attempt unauthorized access, scraping, or disruption of our systems or APIs.',
      'Do not misrepresent farm, location, or account information.',
      'Respect third-party data and service provider terms integrated into Digisaka.',
    ],
  },
  {
    title: 'Suspension and Termination',
    paragraphs: [
      'We may suspend or terminate accounts that violate these Terms or applicable law. Repeated breaches may result in permanent account deletion.',
    ],
  },
  {
    title: 'Limitation of Liability',
    paragraphs: [
      'We strive to provide reliable service but do not guarantee uninterrupted operation or perfect accuracy of maps, forecasts, or analysis outputs.',
      'Agricultural decisions remain your responsibility. Digisaka is a decision-support tool, not a substitute for professional advice.',
      'To the extent permitted by law, we are not liable for indirect or consequential damages arising from use of the Service.',
    ],
  },
  {
    title: 'Modifications to Terms',
    paragraphs: [
      'We may update these Terms from time to time. Changes take effect when posted on the Platform. Continued use after updates signifies acceptance of the revised Terms.',
    ],
  },
  {
    title: 'Contact Information',
    paragraphs: [
      `For questions about these Terms and Conditions, contact us at ${POLICIES_SUPPORT_EMAIL}.`,
    ],
  },
];
